import {
  EnvelopeIcon,
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
  PhoneIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Divider,
  Input,
} from '@heroui/react';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import OAuthButton from '../components/OAuthButton';
import SEOHead from '../components/SEOHead';
import { useAuth } from '../contexts/AuthContext';
import oauthService from '../services/oauth';

const Register: React.FC = () => {
  const pageTitle = 'Create Account';
  const pageDescription = 'Create your CodedGhost Minis account.';
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  const { register, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as any)?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid()) {
      return;
    }

    setIsLoading(true);

    try {
      const { ...registerData } = formData;
      await register(registerData);
      toast.success(
        'Account created successfully! Welcome to CodedGhostMinis!'
      );

      const from = (location.state as any)?.from?.pathname || '/account';
      navigate(from, { replace: true });
    } catch (err: any) {
      toast.error(
        err.response?.data?.message || 'Registration failed. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleOAuthSuccess = () => {
    // OAuth registration will be handled by the callback page
    // This is just for UI feedback
  };

  const handleOAuthError = (_error: string) => {
    // OAuth error handling
  };

  const getPasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const getPasswordStrengthColor = (strength: number) => {
    if (strength < 2) return 'text-danger-500';
    if (strength < 4) return 'text-warning-500';
    return 'text-success-500';
  };

  const getPasswordStrengthText = (strength: number) => {
    if (strength < 2) return 'Weak';
    if (strength < 4) return 'Medium';
    return 'Strong';
  };

  const isFormValid = () => {
    const {
      firstName,
      lastName,
      email,
      password,
      confirmPassword,
      phoneNumber,
    } = formData;

    if (
      !firstName.trim() ||
      !lastName.trim() ||
      !email.trim() ||
      !password.trim() ||
      !phoneNumber.trim()
    ) {
      toast.error('Please fill in all required fields');
      return false;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return false;
    }

    if (!agreeToTerms) {
      toast.error('Please agree to the Terms of Service and Privacy Policy');
      return false;
    }

    return true;
  };

  const passwordStrength = getPasswordStrength(formData.password);

  return (
    <>
      <SEOHead title={pageTitle} description={pageDescription} />
      <div className='min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-md w-full space-y-8'>
          <div className='text-center'>
            <h1 className='text-3xl font-bold text-gray-900'>
              Create your account
            </h1>
            <p className='mt-2 text-sm text-gray-600'>
              Already have an account?{' '}
              <Link
                to='/login'
                className='font-medium text-primary hover:text-primary-500'
              >
                Sign in here
              </Link>
            </p>
          </div>

          <Card className='mt-8'>
            <CardHeader className='pb-2'>
              <h2 className='text-xl font-semibold text-center w-full'>
                Sign Up
              </h2>
            </CardHeader>
            <Divider />
            <CardBody className='space-y-6'>
              {/* OAuth Buttons */}
              <div className='space-y-3'>
                {oauthService.getAvailableProviders().map(provider => (
                  <OAuthButton
                    key={provider.id}
                    provider={provider}
                    mode='register'
                    returnUrl={(location.state as any)?.from?.pathname}
                    onSuccess={handleOAuthSuccess}
                    onError={handleOAuthError}
                  />
                ))}

                {oauthService.getAvailableProviders().length === 0 && (
                  <div className='text-center py-4 text-gray-500'>
                    <p className='text-sm'>Social sign-up is not configured.</p>
                    <p className='text-xs'>
                      Please use email and password to create an account.
                    </p>
                  </div>
                )}
              </div>

              <div className='relative'>
                <div className='absolute inset-0 flex items-center'>
                  <div className='w-full border-t border-gray-300' />
                </div>
                <div className='relative flex justify-center text-sm'>
                  <span className='px-2 bg-white text-gray-500'>
                    Or sign up with email
                  </span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className='space-y-6'>
                <div className='grid grid-cols-2 gap-4'>
                  <Input
                    type='text'
                    label='First Name'
                    placeholder='Enter first name'
                    value={formData.firstName}
                    onChange={e =>
                      handleInputChange('firstName', e.target.value)
                    }
                    required
                    variant='bordered'
                    size='lg'
                    startContent={
                      <UserIcon className='w-4 h-4 text-gray-400' />
                    }
                  />

                  <Input
                    type='text'
                    label='Last Name'
                    placeholder='Enter last name'
                    value={formData.lastName}
                    onChange={e =>
                      handleInputChange('lastName', e.target.value)
                    }
                    required
                    variant='bordered'
                    size='lg'
                    startContent={
                      <UserIcon className='w-4 h-4 text-gray-400' />
                    }
                  />
                </div>

                <Input
                  type='email'
                  label='Email address'
                  placeholder='Enter your email'
                  value={formData.email}
                  onChange={e => handleInputChange('email', e.target.value)}
                  required
                  variant='bordered'
                  size='lg'
                  startContent={
                    <EnvelopeIcon className='w-4 h-4 text-gray-400' />
                  }
                />

                <Input
                  type='tel'
                  label='Phone Number (Required)'
                  placeholder='Enter your phone number'
                  isRequired
                  value={formData.phoneNumber}
                  onChange={e =>
                    handleInputChange('phoneNumber', e.target.value)
                  }
                  variant='bordered'
                  size='lg'
                  startContent={<PhoneIcon className='w-4 h-4 text-gray-400' />}
                />

                <Input
                  type={showPassword ? 'text' : 'password'}
                  label='Password'
                  placeholder='Create a password'
                  value={formData.password}
                  onChange={e => handleInputChange('password', e.target.value)}
                  required
                  variant='bordered'
                  size='lg'
                  startContent={
                    <LockClosedIcon className='w-4 h-4 text-gray-400' />
                  }
                  endContent={
                    <button
                      type='button'
                      onClick={() => setShowPassword(!showPassword)}
                      className='focus:outline-none'
                    >
                      {showPassword ? (
                        <EyeSlashIcon className='w-4 h-4 text-gray-400' />
                      ) : (
                        <EyeIcon className='w-4 h-4 text-gray-400' />
                      )}
                    </button>
                  }
                  description={
                    formData.password && (
                      <span
                        className={getPasswordStrengthColor(passwordStrength)}
                      >
                        Password strength:{' '}
                        {getPasswordStrengthText(passwordStrength)}
                      </span>
                    )
                  }
                />

                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  label='Confirm Password'
                  placeholder='Confirm your password'
                  value={formData.confirmPassword}
                  onChange={e =>
                    handleInputChange('confirmPassword', e.target.value)
                  }
                  required
                  variant='bordered'
                  size='lg'
                  startContent={
                    <LockClosedIcon className='w-4 h-4 text-gray-400' />
                  }
                  endContent={
                    <button
                      type='button'
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className='focus:outline-none'
                    >
                      {showConfirmPassword ? (
                        <EyeSlashIcon className='w-4 h-4 text-gray-400' />
                      ) : (
                        <EyeIcon className='w-4 h-4 text-gray-400' />
                      )}
                    </button>
                  }
                  isInvalid={
                    !!(
                      formData.confirmPassword &&
                      formData.password !== formData.confirmPassword
                    )
                  }
                  errorMessage={
                    formData.confirmPassword &&
                    formData.password !== formData.confirmPassword
                      ? 'Passwords do not match'
                      : ''
                  }
                />

                <div className='space-y-4'>
                  <Checkbox
                    isSelected={agreeToTerms}
                    onValueChange={setAgreeToTerms}
                    size='sm'
                  >
                    <span className='text-sm text-gray-600'>
                      I agree to the{' '}
                      <Link
                        to='/terms'
                        className='text-primary hover:text-primary-500'
                      >
                        Terms of Service
                      </Link>{' '}
                      and{' '}
                      <Link
                        to='/privacy'
                        className='text-primary hover:text-primary-500'
                      >
                        Privacy Policy
                      </Link>
                    </span>
                  </Checkbox>
                </div>

                <Button
                  type='submit'
                  color='primary'
                  size='lg'
                  className='w-full'
                  isLoading={isLoading}
                  isDisabled={!agreeToTerms}
                >
                  Create Account
                </Button>
              </form>

              <div className='mt-4 p-4 bg-gray-50 rounded-lg'>
                <h4 className='font-medium text-gray-900 mb-2'>
                  Password Requirements:
                </h4>
                <ul className='text-sm text-gray-600 space-y-1'>
                  <li className='flex items-center space-x-2'>
                    <div
                      className={`w-2 h-2 rounded-full ${
                        formData.password.length >= 8
                          ? 'bg-success-500'
                          : 'bg-gray-300'
                      }`}
                    />
                    <span>At least 8 characters long</span>
                  </li>
                  <li className='flex items-center space-x-2'>
                    <div
                      className={`w-2 h-2 rounded-full ${
                        /[A-Z]/.test(formData.password)
                          ? 'bg-success-500'
                          : 'bg-gray-300'
                      }`}
                    />
                    <span>Contains uppercase letter</span>
                  </li>
                  <li className='flex items-center space-x-2'>
                    <div
                      className={`w-2 h-2 rounded-full ${
                        /[a-z]/.test(formData.password)
                          ? 'bg-success-500'
                          : 'bg-gray-300'
                      }`}
                    />
                    <span>Contains lowercase letter</span>
                  </li>
                  <li className='flex items-center space-x-2'>
                    <div
                      className={`w-2 h-2 rounded-full ${
                        /[0-9]/.test(formData.password)
                          ? 'bg-success-500'
                          : 'bg-gray-300'
                      }`}
                    />
                    <span>Contains number</span>
                  </li>
                  <li className='flex items-center space-x-2'>
                    <div
                      className={`w-2 h-2 rounded-full ${
                        /[^A-Za-z0-9]/.test(formData.password)
                          ? 'bg-success-500'
                          : 'bg-gray-300'
                      }`}
                    />
                    <span>Contains special character</span>
                  </li>
                </ul>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
};

export default Register;
